import React, { useEffect, useState, useRef } from 'react';
import { ArrowRight, Flame, ShieldCheck } from 'lucide-react';

export interface CtaVariant {
  id?: string;
  variant_key: 'variant_a' | 'variant_b' | 'variant_c' | string;
  category?: string;
  headline: string;
  subheading: string;
  button_text: string;
  link_text: string;
  target_url: string;
  fine_print: string;
}

const STATIC_VARIANTS: Record<string, CtaVariant> = {
  variant_a: {
    id: '10000000-0000-0000-0000-000000000001',
    variant_key: 'variant_a',
    category: 'AI STRATEGY SPRINT',
    headline: 'Sometimes You Need an Adult in the Room.',
    subheading: 'I have spent years watching expensive ideas collide with reality. Bring me in for two focused days to challenge assumptions, pressure-test the strategy, and deliver a decision-ready AI roadmap.',
    button_text: 'Book the AI Strategy Sprint',
    link_text: 'See What’s Included',
    target_url: 'landing-a-adult-in-the-room.html',
    fine_print: 'Two focused days. Fixed scope. Independent judgment. No retainer required.'
  },
  variant_b: {
    id: '10000000-0000-0000-0000-000000000002',
    variant_key: 'variant_b',
    category: 'AI STRATEGY SPRINT',
    headline: 'Two Days In. A Decision-Ready AI Roadmap Out.',
    subheading: 'I have spent years watching expensive ideas collide with reality. Bring me in for two focused days to challenge assumptions, pressure-test the strategy, and deliver a decision-ready AI roadmap.',
    button_text: 'See How It Works',
    link_text: 'Explore Sprint Deliverables',
    target_url: 'landing-b-decision-ready.html',
    fine_print: 'Two focused days. Fixed scope. Independent judgment. No retainer required.'
  },
  variant_c: {
    id: '10000000-0000-0000-0000-000000000003',
    variant_key: 'variant_c',
    category: 'AI STRATEGY SPRINT',
    headline: 'Before You Hire a CAIO, Borrow One.',
    subheading: 'I have spent years watching expensive ideas collide with reality. Bring me in for two focused days to challenge assumptions, pressure-test the strategy, and deliver a decision-ready AI roadmap.',
    button_text: 'See What’s Included',
    link_text: 'Learn About Borrowing a CAIO',
    target_url: 'landing-c-borrow-a-caio.html',
    fine_print: 'Two focused days. Fixed scope. Independent judgment. No retainer required.'
  }
};

interface DynamicCtaProps {
  variantKey?: 'variant_a' | 'variant_b' | 'variant_c' | string;
  mode?: 'auto' | 'fixed';
  className?: string;
  placementLocation?: string;
  showVariantBadge?: boolean;
  onConversion?: (variantKey: string) => void;
}

const getRotatedVariant = (location: string, requestedKey?: string): CtaVariant => {
  if (requestedKey && STATIC_VARIANTS[requestedKey]) {
    return STATIC_VARIANTS[requestedKey];
  }
  const keys = ['variant_a', 'variant_b', 'variant_c'];
  const sessionCount = parseInt(typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('cta_session_rotation') || '0' : '0', 10);
  let locationOffset = 0;
  if (location.includes('post_qa')) locationOffset = 1;
  else if (location.includes('blog') || location.includes('midfeed')) locationOffset = 2;
  
  const chosenKey = keys[(sessionCount + locationOffset) % keys.length];
  return STATIC_VARIANTS[chosenKey] || STATIC_VARIANTS.variant_a;
};

export const DynamicCta: React.FC<DynamicCtaProps> = ({
  variantKey,
  mode = 'auto',
  className = '',
  placementLocation = 'default',
  showVariantBadge = false,
  onConversion
}) => {
  const [cta, setCta] = useState<CtaVariant>(() => {
    return getRotatedVariant(placementLocation, variantKey);
  });

  const [loading, setLoading] = useState(false);
  const ctaRef = useRef<HTMLDivElement>(null);
  const hasLoggedImpression = useRef(false);

  useEffect(() => {
    if (mode === 'fixed' && variantKey && STATIC_VARIANTS[variantKey]) {
      setCta(STATIC_VARIANTS[variantKey]);
      return;
    }

    let principalId = localStorage.getItem('principal_id');
    if (!principalId) {
      principalId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'p-' + Date.now();
      localStorage.setItem('principal_id', principalId);
    }

    // Increment rotation index for successive placements / refreshes
    if (typeof sessionStorage !== 'undefined') {
      const currentRotation = parseInt(sessionStorage.getItem('cta_session_rotation') || '0', 10);
      sessionStorage.setItem('cta_session_rotation', (currentRotation + 1).toString());
    }

    setLoading(true);
    fetch('/api/cta/next', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ principal_id: principalId, location: placementLocation })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.headline && data.variant_key && STATIC_VARIANTS[data.variant_key]) {
          setCta({
            id: data.cta_id,
            variant_key: data.variant_key,
            category: data.category || 'AI STRATEGY SPRINT',
            headline: data.headline,
            subheading: data.subheading,
            button_text: data.button_text || 'See What’s Included',
            link_text: data.link_text || 'Learn More',
            target_url: data.target_url || 'landing-c-borrow-a-caio.html',
            fine_print: data.fine_print || 'Two focused days. Fixed scope. Independent judgment. No retainer required.'
          });
        } else {
          setCta(getRotatedVariant(placementLocation, variantKey));
        }
      })
      .catch((err) => {
        console.warn('Backend CTA rotation offline, serving rotated fallback variant:', err);
        setCta(getRotatedVariant(placementLocation, variantKey));
      })
      .finally(() => setLoading(false));
  }, [mode, variantKey, placementLocation]);

  // Viewport Impression Tracking via IntersectionObserver with Session Deduplication
  useEffect(() => {
    if (typeof window === 'undefined' || hasLoggedImpression.current) return;

    const logImpression = () => {
      const sessionKey = `cta_viewed_${placementLocation}_${cta.variant_key}`;
      if (sessionStorage.getItem(sessionKey)) return;

      sessionStorage.setItem(sessionKey, 'true');
      hasLoggedImpression.current = true;

      const principalId = localStorage.getItem('principal_id') || 'anonymous';
      const payload = {
        event: 'cta_impression',
        variant_id: cta.variant_key,
        location: placementLocation,
        timestamp: new Date().toISOString(),
        principal_id: principalId,
        cta_id: cta.id
      };

      fetch('/api/telemetry/impression', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch((e) => console.error('Failed to log telemetry impression', e));

      // Also trigger legacy endpoint for backward compatibility
      if (cta.id) {
        fetch('/api/cta/impression', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ principal_id: principalId, cta_id: cta.id }),
          keepalive: true
        }).catch(() => {});
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            logImpression();
            observer.disconnect();
          }
        },
        { threshold: 0.4 } // Must be 40% visible in viewport
      );

      if (ctaRef.current) {
        observer.observe(ctaRef.current);
      }

      return () => observer.disconnect();
    } else {
      // Fallback for browsers without IntersectionObserver
      logImpression();
    }
  }, [cta, placementLocation]);

  const handleCtaClick = (isPrimary: boolean) => {
    const principalId = localStorage.getItem('principal_id') || 'anonymous';
    const clickPayload = {
      event: 'cta_click',
      variant_id: cta.variant_key,
      location: placementLocation,
      timestamp: new Date().toISOString(),
      principal_id: principalId,
      cta_id: cta.id
    };

    fetch('/api/telemetry/click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(clickPayload),
      keepalive: true
    }).catch((e) => console.error('Failed to log telemetry click', e));

    if (cta.id) {
      fetch('/api/cta/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ principal_id: principalId, cta_id: cta.id, location: placementLocation }),
        keepalive: true
      }).catch((e) => console.error('Failed to log conversion telemetry', e));
    }

    if (onConversion) {
      onConversion(cta.variant_key);
    }

    if (cta.target_url) {
      window.location.href = cta.target_url;
    }
  };

  const getVariantBadge = (key: string) => {
    switch (key) {
      case 'variant_a':
        return { label: 'VARIANT A — AUTHORITY', color: 'border-brand-burgundy text-brand-burgundy bg-brand-sand/60' };
      case 'variant_b':
        return { label: 'VARIANT B — OUTCOME', color: 'border-brand-orange text-brand-orange bg-brand-sand/60' };
      case 'variant_c':
      default:
        return { label: 'VARIANT C — LOW-COMMITMENT LEADERSHIP', color: 'border-brand-green text-brand-green bg-brand-sand/60' };
    }
  };

  const badge = getVariantBadge(cta.variant_key);

  return (
    <div
      ref={ctaRef}
      className={`bg-brand-cream border-2 border-brand-sand rounded-sm p-8 md:p-10 relative shadow-md text-brand-charcoal overflow-hidden ${className}`}
    >
      {/* Decorative Corner Brackets matching site style */}
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-brand-burgundy" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-brand-burgundy" />

      {/* Header Eyebrow Plaque */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="inline-flex items-center gap-2.5 px-3 py-1 bg-brand-sand border-l-4 border-brand-burgundy rounded-r-sm">
          <Flame className="w-3.5 h-3.5 text-brand-orange animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-wider font-bold text-brand-burgundy">
            {cta.category || 'AI STRATEGY SPRINT — $5,200'}
          </span>
        </div>
        {showVariantBadge && (
          <span className={`font-mono text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-sm border uppercase ${badge.color}`}>
            {badge.label}
          </span>
        )}
      </div>

      {/* Headline in Editorial Serif (Arvo / font-display) */}
      <h2 className="text-2xl md:text-4xl font-display font-bold text-brand-charcoal tracking-tight leading-tight mb-4 max-w-3xl">
        {cta.headline}
      </h2>

      {/* Subhead in Plus Jakarta Sans */}
      <p className="text-base text-stone-600 font-sans leading-relaxed max-w-2xl mb-8 font-light">
        {cta.subheading}
      </p>

      {/* Action Buttons styled per brand guide */}
      <div className="flex flex-wrap items-center gap-5 mb-6">
        <button
          type="button"
          onClick={() => handleCtaClick(true)}
          className="bg-brand-burgundy hover:bg-brand-orange text-white font-mono text-xs md:text-sm font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm shadow-md transition-colors flex items-center gap-2 cursor-pointer"
        >
          {cta.button_text}
          <ArrowRight className="w-4 h-4 text-brand-orange" />
        </button>

        <button
          type="button"
          onClick={() => handleCtaClick(false)}
          className="font-mono text-xs md:text-sm font-bold text-brand-burgundy hover:text-brand-orange underline underline-offset-4 cursor-pointer transition-colors"
        >
          [{cta.link_text}]
        </button>
      </div>

      {/* Fine Print in JetBrains Mono */}
      <div className="pt-5 border-t border-brand-sand flex items-center gap-2 font-mono text-xs text-stone-500">
        <ShieldCheck className="w-4 h-4 text-brand-green flex-shrink-0" />
        <span>{cta.fine_print}</span>
      </div>
    </div>
  );
};

export default DynamicCta;
